import {Component, inject, signal, WritableSignal} from '@angular/core';
import {ButtonModule} from "primeng/button";
import {Navbar} from "../../../../shared/components/navbar/navbar";
import {Card} from '../../../../shared/components/card/card/card';
import {CardHeader} from '../../../../shared/components/card/card-header/card-header';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MessageService} from 'primeng/api';
import {UserService} from '../../../../services/user.service';
import {ActivatedRoute, Router} from '@angular/router';
import {InputText} from 'primeng/inputtext';
import {Message} from 'primeng/message';
import { UserStatus, type CreateUserPayload, type UpdateUserPayload } from '../../../../interfaces/user';
import { finalize } from 'rxjs';
import type { HttpErrorResponse } from '@angular/common/http';
import { InputMaskModule } from 'primeng/inputmask';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../../services/auth.service';
import { RoleEnum } from '../../../../interfaces/application';
import { RolePipe } from "../../../../shared/pipes/role-pipe";
import { SelectModule } from 'primeng/select';
import { DividerModule } from 'primeng/divider';
import { TableActivityLogs } from "../../../../shared/tables/table-activity-logs/table-activity-logs";

@Component({
  selector: 'app-user-form',
  imports: [
    CommonModule,
    Navbar,
    Card,
    CardHeader,
    ReactiveFormsModule,
    InputText,
    Message,
    ButtonModule,
    InputMaskModule,
    SelectButtonModule,
    SelectModule,
    RolePipe,
    DividerModule,
    TableActivityLogs
],
  templateUrl: './user-form.html',
  styleUrl: './user-form.css'
})
export class UserForm {

  private readonly _fb: FormBuilder = inject(FormBuilder);
  private readonly _router: Router = inject(Router);
  private readonly _activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private readonly _messageService: MessageService = inject(MessageService);
  private readonly _userService: UserService = inject(UserService);
  private readonly _authService: AuthService = inject(AuthService);

  protected userStatus = UserStatus;
  protected roleOptions: { label: RoleEnum; value: RoleEnum }[] = [
    RoleEnum.ADMIN,
    RoleEnum.FINANCIAL,
    RoleEnum.JURIDICAL,
    RoleEnum.MARKET,
    RoleEnum.TELEMARKETING,
    RoleEnum.AFFILIATE,
    RoleEnum.OPERATOR,
    RoleEnum.VIEWER,
  ].map(role => ({ label: role, value: role }));

  public title: WritableSignal<string> = signal<string>('');
  public loading: WritableSignal<boolean> = signal<boolean>(false);

  public isMasterAdmin: WritableSignal<boolean> = signal<boolean>(false);
  public readonly: WritableSignal<boolean> = signal<boolean>(false);
  public userId: WritableSignal<string> = signal<string>('');

  public newUserForm: FormGroup = this._fb.group({
    email: [null, [Validators.required, Validators.email]],
    role: [null, [Validators.required]],
    utm_afiliados_id: [null]
  });

  public updateUserForm: FormGroup = this._fb.group({
    id: [null, [Validators.required]],
    name: [null, [Validators.required]],
    email: [{ value: null, disabled: true }, [Validators.required, Validators.email]],
    role: [null, [Validators.required]],
    phone: [null, [Validators.required]],
    document: [null, [Validators.required]],
    status: [null, [Validators.required]],
    utm_afiliados_id: [null],
  });

  get updateUserId(): string {
    return this.updateUserForm.get('id')?.value;
  }

  get updateUserName(): string {
    return this.updateUserForm.get('name')?.value;
  }

  get updateUserEmail(): string {
    return this.updateUserForm.get('email')?.value;
  }

  get updateUserStatus(): string {
    return this.updateUserForm.get('status')?.value;
  }

  public ngOnInit(): void {
    const { id } = this._activatedRoute.snapshot.params;
    const { readonly } = this._activatedRoute.snapshot.queryParams;

    this.readonly.set(readonly && readonly === 'true');
    this.userId.set(id);

    this._getAuthenticatedUser();
  }

  private _toggleLoading(): void {
    this.loading.set(!this.loading());
  }

  public cancel(): void {
    this._router.navigate(['users']);
  }

  private _getAuthenticatedUser(): void {
    this._userService.me()
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.isMasterAdmin.set(response.data?.roles.some(role => role.name === RoleEnum.MASTER));

            if (this.userId() === 'new') {
              if (!response.data?.roles.some(role => role.permissions.some(p => p.name === 'invite users'))) {
                this._router.navigate(['forbidden']);
              }
              return;
            }
            this._getUser(this.userId());
          }
        },
      })
  }

  public submitNewUser(): void {
    if (!this.newUserForm.valid) {
      this.newUserForm.markAllAsDirty();
      this._messageService.add({
        severity: 'warn',
        summary: 'Preencha todos os campos obrigatórios',
        life: 3000
      });

      return;
    }

    this._create(this.newUserForm.getRawValue());
  }

  public submitUpdateUser(): void {
    if (!this.updateUserForm.valid) {
      this.updateUserForm.markAllAsDirty();
      this._messageService.add({
        severity: 'warn',
        summary: 'Preencha todos os campos obrigatórios',
        life: 3000
      });

      return;
    }

    this._update(this.userId(), this.updateUserForm.getRawValue());
  }

  private _getUser(id: string): void {
    this._toggleLoading();

    this._userService.show(id)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.updateUserForm.patchValue({
              ...response.data,
              role: response.data.roles[0].name
            });

            if (this.readonly() || response.data.status === UserStatus.PENDING) {
              this.updateUserForm.disable();
            }
          }
        },
        error: (error: HttpErrorResponse) => {
          this._messageService.add({
            severity: 'error',
            summary: 'Falha ao buscar um usuário',
            detail: error.error.message,
            life: 3000
          });

          this._router.navigate(['users']);
        }
      })
  }

  private _create(user: CreateUserPayload): void {
    this._toggleLoading();

    this._userService.post(user)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });
            this._router.navigate(['users']);
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao criar um usuário',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  private _update(id: string, payload: UpdateUserPayload): void {
    this._toggleLoading();

    this._userService.put(id, payload)
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao atualizar um usuário',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public resendInviteEmail(): void {
    this._toggleLoading();

    this._userService.resendInviteEmail(this.userId())
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao enviar email de convite',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

  public sendResetPassword(): void {
    this._toggleLoading();

    this._authService.recoverPassword({ email: this.updateUserForm.get('email')?.value })
      .pipe(finalize(() => this._toggleLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this._messageService.add({
              severity: 'success',
              summary: response.message,
              life: 3000
            });
          }
        },
        error: (error: HttpErrorResponse) => this._messageService.add({
          severity: 'error',
          summary: 'Falha ao enviar email de recuperação de senha',
          detail: error.error.message,
          life: 3000
        }),
      })
  }

}
